import React, { useEffect, useState } from 'react'
import { List, Box, Text } from 'grommet'
import UnifiedAlarmSummary from './UnifiedAlarmSummary'
import { useSelector } from 'react-redux'
import { AlarmUtils, DateTimeUtils } from 'galarm-shared'
import { Constants, GlobalConfig, I18n } from 'galarm-config'
import SearchBar from './SearchBar'

const AlarmList = ({ alarms, showSearchBar, hideSearchBar }) => {
  const [searchText, setSearchText] = useState('')
  const missedAlarmPlacementSetting = useSelector(
    state => state.userSettings.missedAlarmPlacementSetting
  )

  useEffect(() => {
    if (!showSearchBar) {
      setSearchText('')
    }
  }, [showSearchBar])

  let filteredAlarms = alarms.filter(
    alarm =>
      alarm.name.toLowerCase().includes(searchText.toLowerCase()) ||
      alarm.notes.toLowerCase().includes(searchText.toLowerCase())
  )

  const currDate = Date.now()

  let alarmsObj = {}
  alarmsObj[Constants.AlarmTimeCategories.MISSED] = []
  alarmsObj[Constants.AlarmTimeCategories.UPCOMING] = []
  alarmsObj[Constants.AlarmTimeCategories.LATER] = []
  alarmsObj[Constants.AlarmTimeCategories.OLD] = []

  // The overall alarms list is sorted by each alarm's current/effective date,
  // which for a missed alarm is its past (missed) occurrence rather than its
  // next future occurrence. So upcoming/later entries can't just inherit that
  // order - collect them with their real next date and sort separately below.
  const upcomingAlarmsWithSortValue = []
  const laterAlarmsWithSortValue = []

  filteredAlarms.forEach(alarm => {
    let alarmEffectiveDate
    let alarmStatus
    let currAlarmDate
    if (alarm.alarmCategory === Constants.AlarmCategories.MY_ALARM) {
      currAlarmDate = AlarmUtils.getCurrentDateForAlarm(alarm)
      alarmEffectiveDate = DateTimeUtils.getMyAlarmDate(currAlarmDate)
      alarmStatus = alarm.status
    } else {
      currAlarmDate = AlarmUtils.getCurrentDateForAlarm(alarm)
      alarmEffectiveDate = DateTimeUtils.getParticipantAlarmDate(
        { ...alarm, date: currAlarmDate },
        alarm.order
      )
      alarmStatus =
        alarm.status && alarm.responseStatus !== Constants.REJECT_ALARM
    }

    const nextAlarmDate = AlarmUtils.getNextDateForAlarmWithSnooze(alarm)

    if (
      alarmStatus &&
      nextAlarmDate > currDate + GlobalConfig.upcomingAlarmInterval
    ) {
      laterAlarmsWithSortValue.push({
        alarm: { ...alarm, showNextDate: true },
        sortValue: nextAlarmDate
      })
    } else if (alarmStatus && nextAlarmDate > currDate) {
      upcomingAlarmsWithSortValue.push({
        alarm: { ...alarm, showNextDate: true },
        sortValue: nextAlarmDate
      })
    }

    const alarmInSnooze =
      alarm.alarmCategory === Constants.AlarmCategories.MY_ALARM
        ? AlarmUtils.isLastSnoozeTimeInSnoozeDurationForMyAlarm(alarm)
        : AlarmUtils.isLastSnoozeTimeInSnoozeDurationForParticipantAlarm(alarm)
    const isPastOccurrence =
      alarmStatus && !alarmInSnooze && alarmEffectiveDate < currDate
    const currentOccurrenceAcknowledged =
      isPastOccurrence &&
      !!AlarmUtils.getAlarmAcknowledgementStatusForOccurrenceForAlarm(
        alarm,
        currAlarmDate
      )
    if (isPastOccurrence && !currentOccurrenceAcknowledged) {
      alarmsObj[Constants.AlarmTimeCategories.MISSED].push(alarm)
    }

    if (!alarmStatus || currentOccurrenceAcknowledged) {
      alarmsObj[Constants.AlarmTimeCategories.OLD].push(alarm)
    }
  })

  // Sort by each alarm's actual next occurrence date (ascending), not by the
  // overall list order, so missed alarms don't jump to the top based on their
  // old missed date instead of their upcoming one
  alarmsObj[Constants.AlarmTimeCategories.UPCOMING] =
    upcomingAlarmsWithSortValue
      .sort((a, b) => a.sortValue - b.sortValue)
      .map(({ alarm }) => alarm)
  alarmsObj[Constants.AlarmTimeCategories.LATER] = laterAlarmsWithSortValue
    .sort((a, b) => a.sortValue - b.sortValue)
    .map(({ alarm }) => alarm)

  if (
    missedAlarmPlacementSetting ===
      Constants.MISSED_ALARMS_PLACEMENT_SETTINGS_OPTIONS.SHOW_NOTIFICATION ||
    missedAlarmPlacementSetting ===
      Constants.MISSED_ALARMS_PLACEMENT_SETTINGS_OPTIONS.DONT_SHOW
  ) {
    delete alarmsObj[Constants.AlarmTimeCategories.MISSED]
  }

  if (
    alarmsObj[Constants.AlarmTimeCategories.UPCOMING].length === 0 &&
    (alarmsObj[Constants.AlarmTimeCategories.MISSED]?.length || 0) === 0
  ) {
    alarmsObj[Constants.AlarmTimeCategories.UPCOMING] = [
      {
        infoMessage: I18n.t('noUpcomingAlarms')
      }
    ]
  } else if (alarmsObj[Constants.AlarmTimeCategories.UPCOMING].length === 0) {
    delete alarmsObj[Constants.AlarmTimeCategories.UPCOMING]
  }

  if (alarmsObj[Constants.AlarmTimeCategories.LATER].length === 0) {
    delete alarmsObj[Constants.AlarmTimeCategories.LATER]
  }

  if (alarmsObj[Constants.AlarmTimeCategories.OLD].length === 0) {
    delete alarmsObj[Constants.AlarmTimeCategories.OLD]
  }

  if ((alarmsObj[Constants.AlarmTimeCategories.MISSED]?.length || 0) === 0) {
    delete alarmsObj[Constants.AlarmTimeCategories.MISSED]
  }

  const orderedSections =
    missedAlarmPlacementSetting ===
    Constants.MISSED_ALARMS_PLACEMENT_SETTINGS_OPTIONS.SHOW_AFTER_UPCOMING
      ? [
          Constants.AlarmTimeCategories.UPCOMING,
          Constants.AlarmTimeCategories.MISSED,
          Constants.AlarmTimeCategories.LATER,
          Constants.AlarmTimeCategories.OLD
        ]
      : [
          Constants.AlarmTimeCategories.MISSED,
          Constants.AlarmTimeCategories.UPCOMING,
          Constants.AlarmTimeCategories.LATER,
          Constants.AlarmTimeCategories.OLD
        ]

  let alarmsData = []
  orderedSections.forEach(timeCategory => {
    if (!alarmsObj[timeCategory]) {
      return
    }
    alarmsData.push({
      sectionName: timeCategory
    })
    alarmsData = alarmsData.concat(alarmsObj[timeCategory])
  })

  return (
    <Box flex={{ shrink: 0 }} style={{ paddingBottom: '70px' }}>
      {showSearchBar && (
        <SearchBar
          onChangeText={setSearchText}
          searchText={searchText}
          onCloseSearchBar={hideSearchBar}
          focus
        />
      )}
      {filteredAlarms.length === 0 ? (
        <Box pad="medium" flex justify="center" align="center">
          <Text color="textColor">{I18n.t('noAlarms')}</Text>
        </Box>
      ) : (
        <List
          border={false}
          margin={{ vertical: 'small' }}
          pad={{ vertical: 'xsmall', horizontal: 'small' }}
          data={alarmsData}
          // eslint-disable-next-line react/no-children-prop
          children={UnifiedAlarmSummary}
        />
      )}
    </Box>
  )
}

export default AlarmList
